/**
 * Route-guards via wrapper-components. React Router v6 doet geen
 * built-in route-meta zoals vue-router; we modelleren het via
 * compositie: `<RequireAuth><...children/></RequireAuth>`.
 *
 *  - `<RequireAuth>` redirect naar /login als geen user
 *  - `<RequireRole role="...">` redirect naar / als user mist de rol
 *  - `<GuestOnly>` redirect ingelogde users weg van /login
 *
 * Loading-state: terwijl `auth.fetchMe()` loopt, geven we een lege
 * placeholder. De auth-store wordt gehydrateerd door `useAuthMe()` in
 * App.tsx's bootstrap-flow, dus dit is doorgaans een korte flash.
 */
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@shared/stores/auth';
import { mayGradeTests } from '@shared/mayGradeTests';
import { useTenantMeta } from '@shared/hooks/useTenantMeta';

export function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);
  const location = useLocation();

  // Wacht tot de eerste fetchMe-poging is gedaan voordat we beslissen.
  // Anders ziet een refreshing tenant-admin meteen /login terwijl
  // /auth/me nog onderweg is (de "refresh → login-flash" bug).
  if (!hydrated) {
    return <div aria-busy="true" />;
  }
  if (user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

/**
 * Beheer-console-guard (ADR-0028): admin-views zijn alleen bereikbaar op de
 * beheer-host (`surface === 'manage'`) én voor wie het recht **`admin_console.enter`**
 * heeft. Een gebruiker die op de eindgebruiker-host (`app`) naar `/users` deep-linkt,
 * wordt naar het dashboard teruggestuurd — de admin-tooling hoort op `manage.<host>`.
 *
 * Sinds #277 hangt dit aan een **recht**, niet aan de rolnaam `tenant-admin`: welke rol
 * de console mag betreden is per omgeving in te richten, en "beheer-oppervlak" is geen
 * eigenschap die je uit losse rechten kunt afleiden — rechten als "gebruikers bekijken"
 * dienen zowel de console als de app-kant. Vandaar één eigen recht ervoor.
 *
 * **`permission` is het atoom van dít scherm** (de SPA-sweep, UI8). `admin_console.enter`
 * alleen was hetzelfde recht voor élke beheer-route: een custom rol met `role.view` kwam
 * de console binnen en kon `/mail-outbox` openen, om daar een muur van 403's te krijgen.
 * De API was veilig, het scherm loog. De afbeelding route→atoom komt uit de route-sweep
 * van Inc 2.
 *
 * Bewust één guard met een extra prop in plaats van twee geneste wrappers: een vergeten
 * buitenste wrapper is een gat, en dat risico wil je niet 25 keer aangaan. Dit is de
 * UX-laag — de grens zelf blijft de API.
 *
 * Een scherm dat twee rechten bedient (de regels bij een training: evaluaties én toetsen) geeft
 * een **lijst**: elk van die rechten laat je binnen. Een lege lijst laat niemand binnen.
 */
export function RequireAdminConsole({
  children,
  permission,
}: {
  children: ReactNode;
  permission?: string | readonly string[];
}) {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);
  const metaQuery = useTenantMeta();

  // Wacht op zowel auth- als meta-hydratie voordat we (mogen) redirecten —
  // anders flikkert een geldige admin even weg tijdens het laden.
  if (!hydrated || metaQuery.isLoading) {
    return <div aria-busy="true" />;
  }
  if (user === null) {
    return <Navigate to="/login" replace />;
  }
  const held = user.permissions ?? [];
  if (!held.includes('admin_console.enter')) {
    return <Navigate to="/" replace />;
  }
  if (permission !== undefined && !(typeof permission === 'string' ? [permission] : permission).some((atom) => held.includes(atom))) {
    return <Navigate to="/" replace />;
  }
  if (metaQuery.data?.surface !== 'manage') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * Alleen een **recht**, geen beheerconsole en geen host-eis.
 *
 * Waarom dit náást {@see RequireAdminConsole} bestaat en niet als variant erop:
 * niet elk recht hoort bij de console. Presentie is het voorbeeld — die legt de
 * docent vast, en een docent is geen beheerder: het `teacher`-sjabloon heeft
 * `admin_console.enter` niet, en dat hoort zo te blijven (RG22 — de console is een
 * deur, geen bevoegdheid). Zou dit scherm achter die guard hangen, dan kon precies
 * de rol die het werk doet er niet bij.
 *
 * Dit is de UX-laag; de grens zelf blijft de API (route-atoom + ScopeResolver).
 */
export function RequirePermission({
  children,
  permission,
}: {
  children: ReactNode;
  permission: string;
}) {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);

  // Wacht op hydratie voordat we (mogen) redirecten — anders flikkert een geldige
  // houder even weg tijdens het laden.
  if (!hydrated) {
    return <div aria-busy="true" />;
  }
  if (user === null) {
    return <Navigate to="/login" replace />;
  }
  if (!(user.permissions ?? []).includes(permission)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * De nakijk-schermen: het recht `test.grade`, óf aangewezen als nakijker van een toets
 * (besluit PO 16-10, {@link mayGradeTests}). Een eigen guard en geen variant van
 * {@see RequirePermission}: de aanwijzing is geen recht, en een docent zonder het atoom en
 * zonder aanwijzing hoort er nog steeds niet in.
 *
 * Ook hier de UX-laag; welke toets je ziet beslist de API.
 */
export function RequireGrader({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);

  if (!hydrated) {
    return <div aria-busy="true" />;
  }
  if (user === null) {
    return <Navigate to="/login" replace />;
  }
  if (!mayGradeTests(user)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function GuestOnly({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);

  // Zelfde reden als RequireAuth: blokkeer de redirect-naar-/ tot we
  // weten dat er écht geen sessie is. Vermijdt dat een net-ingelogde
  // tenant-admin op /login terechtkomt wanneer hij de URL deelt of de
  // pagina refresht — fetchMe lost dat alsnog op via een Navigate.
  if (!hydrated) {
    return <div aria-busy="true" />;
  }
  if (user !== null) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
